import { memo, useCallback, useMemo } from 'react';
import { Box, Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { HighwayArea, HighwayPoiSpec } from '@thive/fddm-types';
import { MathUtils } from 'three';

import MarginHorizontalIcon from '@/assets/icons/MarginHorizontalIcon';
import MarginVerticalIcon from '@/assets/icons/MarginVerticalIcon';
import { GapHorizontalIcon, GapVerticalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumUtils } from '@/modules/common/utils';
import { SelectInput } from '@/modules/common/components/inputs/components/SelectInput';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isHighwayArea } from '@/modules/floorplanDesign/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

const stackSx = { width: '100%' };

const DISTRIBUTION_OPTIONS: {
  label: string;
  value:
    | HighwayPoiSpec['horizontalDistribution']
    | HighwayPoiSpec['verticalDistribution'];
}[] = [
  { label: 'Margin', value: 'MARGIN' },
  { label: 'Gap', value: 'GAP' },
];

function HighwaySpacingComponent() {
  const { t } = useTranslation(['interface']);
  const selectedAreas = useReadSelectedAreas();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const selectedHighwayArea = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;

    const area = areas[0];
    if (!isHighwayArea(area)) return;

    return area;
  }, [selectedAreas]);

  const highwayIconRotationDeg = useMemo(() => {
    if (!selectedHighwayArea) return 0;
    if (selectedHighwayArea.segments.length > 1) return 0

    return MathUtils.radToDeg(RectangleUtils.fromSegment(selectedHighwayArea.segments[0]).angle) * -1;
  }, [selectedHighwayArea]);

  const iconSx = useMemo(
    () => ({
      display: 'inline-flex',
      lineHeight: 0,
      transform: `rotate(${highwayIconRotationDeg}deg)`,
      transformOrigin: 'center',
    }),
    [highwayIconRotationDeg],
  );

  const horizontalMarginIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <MarginHorizontalIcon />
      </Box>
    ),
    [iconSx],
  );

  const horizontalGapIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <GapHorizontalIcon />
      </Box>
    ),
    [iconSx],
  );

  const verticalMarginIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <MarginVerticalIcon />
      </Box>
    ),
    [iconSx],
  );

  const verticalGapIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <GapVerticalIcon />
      </Box>
    ),
    [iconSx],
  );

  const onHorizontalDistributionChange = useCallback(
    (newValue: HighwayPoiSpec['horizontalDistribution']) => {
      if (!selectedHighwayArea) return;

      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.horizontalDistribution', newValue);

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] =
        minItemsArrayUtils.mapAtLeast1(selectedHighwayArea.highwayPoiSpecs, (spec) => ({
          ...spec,
          horizontalDistribution: newValue,
          
        }));
      fddmSvc.updateArea({
        ...selectedHighwayArea,
        highwayPoiSpecs: updatedHighwayPoiSpecs
      });
    },
    [fddmSvc, selectedHighwayArea],
  );

  const onHorizontalMinMarginChange = useCallback(
    (newValueMm: number) => {
      if (!selectedHighwayArea) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.horizontalSpacing.minMargin', newValue);

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] =
        minItemsArrayUtils.mapAtLeast1(selectedHighwayArea.highwayPoiSpecs, (spec) => ({
          ...spec,
          horizontalSpacing: {
              ...spec.horizontalSpacing,
              minMargin: newValue,
          },
        }));
      fddmSvc.updateArea({
        ...selectedHighwayArea,
        highwayPoiSpecs: updatedHighwayPoiSpecs,
      });
    },
    [fddmSvc, selectedHighwayArea],
  );

  const onHorizontalMinGapChange = useCallback(
    (newValueMm: number) => {
      if (!selectedHighwayArea) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.horizontalSpacing.minGap', newValue);

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] =
        minItemsArrayUtils.mapAtLeast1(selectedHighwayArea.highwayPoiSpecs, (spec) => ({
          ...spec,
          horizontalSpacing: {
              ...spec.horizontalSpacing,
              minGap: newValue,
          },
        }));
      fddmSvc.updateArea({
        ...selectedHighwayArea,
        highwayPoiSpecs: updatedHighwayPoiSpecs,
      });
    },
    [fddmSvc, selectedHighwayArea],
  );

  const onVerticalDistributionChange = useCallback(
    (newValue: HighwayPoiSpec['verticalDistribution']) => {
      if (!selectedHighwayArea) return;

      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.verticalDistribution', newValue);

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] = minItemsArrayUtils.mapAtLeast1(
        selectedHighwayArea.highwayPoiSpecs,
        (spec) => ({
          ...spec,
          verticalDistribution: newValue,
        }),
      );
      fddmSvc.updateArea({
        ...selectedHighwayArea,
        highwayPoiSpecs: updatedHighwayPoiSpecs,
      });
    },
    [fddmSvc, selectedHighwayArea],
  );

  const onVerticalMinMarginChange = useCallback(
    (newValueMm: number) => {
      if (!selectedHighwayArea) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.verticalSpacing.minMargin', newValue);

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] =
        minItemsArrayUtils.mapAtLeast1(selectedHighwayArea.highwayPoiSpecs, (spec) => ({
          ...spec,
          verticalSpacing: {
            ...spec.verticalSpacing,
            minMargin: newValue,
          },
        }));
      fddmSvc.updateArea({
        ...selectedHighwayArea,
        highwayPoiSpecs: updatedHighwayPoiSpecs,
      });
    },
    [fddmSvc, selectedHighwayArea],
  );

  const onVerticalMinGapChange = useCallback(
    (newValueMm: number) => {
      if (!selectedHighwayArea) return;

      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.verticalSpacing.minGap', newValue);

      const updatedHighwayPoiSpecs: HighwayArea['highwayPoiSpecs'] =
        minItemsArrayUtils.mapAtLeast1(selectedHighwayArea.highwayPoiSpecs, (spec) => ({
          ...spec,
          verticalSpacing: {
              ...spec.verticalSpacing,
              minGap: newValue,
          },
        }));
      fddmSvc.updateArea({
        ...selectedHighwayArea,
        highwayPoiSpecs: updatedHighwayPoiSpecs,
      });
    },
    [fddmSvc, selectedHighwayArea],
  );

  const highwayPoiSpec = selectedHighwayArea?.highwayPoiSpecs[0];
  const verticalSpacing = highwayPoiSpec?.verticalSpacing;
  const horizontalSpacing = highwayPoiSpec?.horizontalSpacing;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.horizontal_distribution'>
        <SelectInput
          options={DISTRIBUTION_OPTIONS}
          onChange={onHorizontalDistributionChange}
          value={highwayPoiSpec?.horizontalDistribution}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.horizontal_spacing'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          spacing={1}
          sx={stackSx}
        >
          <NumberInput
            value={NumUtils.metersToMmRounded(horizontalSpacing?.minMargin ?? 0)}
            unit='mm'
            prefix={horizontalMarginIcon}
            onChange={onHorizontalMinMarginChange}
            tooltip={t('properties.highway.tooltips.routing_point_margin_to_crossing')}
          />
          <NumberInput
            value={NumUtils.metersToMmRounded(horizontalSpacing?.minGap ?? 0)}
            unit='mm'
            prefix={horizontalGapIcon}
            onChange={onHorizontalMinGapChange}
            tooltip={t('properties.highway.tooltips.routing_point_group_min_gap')}
          />
        </Stack>
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.vertical_distribution'>
        <SelectInput
          options={DISTRIBUTION_OPTIONS}
          onChange={onVerticalDistributionChange}
          value={highwayPoiSpec?.verticalDistribution}
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.vertical_spacing'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          spacing={1}
          sx={stackSx}
        >
          <NumberInput
            value={NumUtils.metersToMmRounded(verticalSpacing?.minMargin ?? 0)}
            unit='mm'
            prefix={verticalMarginIcon}
            onChange={onVerticalMinMarginChange}
            tooltip={t('properties.highway.tooltips.margin')}
          />
          <NumberInput
            value={NumUtils.metersToMmRounded(verticalSpacing?.minGap ?? 0)}
            unit='mm'
            prefix={verticalGapIcon}
            onChange={onVerticalMinGapChange}
            tooltip={t('properties.highway.tooltips.gap')}
          />
        </Stack>
      </PropertiesLabel>
    </>
  );
}

export const HighwaySpacing = memo(HighwaySpacingComponent);
